import { User } from '@sr/types';
import sortBy from 'lodash/sortBy';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  ArrowRightIcon,
  Box,
  Button,
  CheckIcon,
  CopyIcon,
  Dropdown,
  EditIcon,
  Flex,
  Link,
  MoreIcon,
  Text,
  TrashIcon,
} from '../../ui';
import API from '../../utils/api';
import ResumeMetrics from './ResumeMetrics';

type Props = {
  user: User;
  closeMenu?: () => void;
};

const ResumesList = ({ user, closeMenu }: Props) => {
  const history = useHistory();
  const { resumeId: activeResumeId } = useParams<{ resumeId: string }>();
  // Nothing to show if the user has no resumes
  if (!user.resumes) return null;

  // Sort resumes alphabetically by their names
  const resumeEntries: [string, string][] = sortBy(
    Object.entries(user.resumes),
    (entry) => entry[1]
  );

  function handleDeleteClick(resumeId: string) {
    if (window.confirm('Are you sure that you want to delete this resume?')) {
      API.resume.delete({ resumeId, user }).finally(() => {
        history.replace('/resumes');
      });
    }
  }

  function handleDuplicateClick(resumeId: string) {
    API.resume.duplicate({ resumeId });
  }

  return (
    <Box>
      {resumeEntries.map(([id, name], index) => (
        <Flex
          key={id}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            ':hover .moreIcon': { opacity: 1 },
            ':hover .arrowIcon': { opacity: 1 },
          }}
          py={2}
          id={`gi-resume-${index}`}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Link to={`/resumes/${id}`} onClick={closeMenu}>
              <Text variant="titleDark" mb={1}>
                {name}
              </Text>
              <ResumeMetrics resumeId={id} />
            </Link>
          </Box>
          <Flex sx={{ alighItems: 'center' }}>
            <Dropdown
              target={
                <MoreIcon
                  color="white50"
                  mr={3}
                  className="moreIcon"
                  sx={{ opacity: 0, transition: 'opacity 200ms linear' }}
                />
              }
            >
              <Button
                variant="link"
                sx={{
                  color: 'black',
                  borderColor: 'buttonGray',
                  borderBottomStyle: 'solid',
                  borderBottomWidth: 1,
                  width: '100%',
                }}
                onClick={() => handleRenameClick(id)}
              >
                <EditIcon mr={2} />
                Rename resume
              </Button>
              <Button
                variant="link"
                sx={{
                  color: 'black',
                  borderColor: 'buttonGray',
                  borderBottomStyle: 'solid',
                  borderBottomWidth: 1,
                  width: '100%',
                }}
                onClick={() => handleDuplicateClick(id)}
              >
                <CopyIcon mr={2} />
                Duplicate resume
              </Button>
              <Button
                variant="link"
                sx={{ color: 'red', width: '100%' }}
                onClick={() => handleDeleteClick(id)}
              >
                <TrashIcon color="red" mr={2} />
                Delete resume
              </Button>
            </Dropdown>
            {activeResumeId === id ? (
              <CheckIcon color="white" />
            ) : (
              <Link
                to={`/resumes/${id}`}
                onClick={closeMenu}
                className="arrowIcon"
                sx={{ opacity: 0, transition: 'opacity 200ms linear' }}
              >
                <ArrowRightIcon color="white" />
              </Link>
            )}
          </Flex>
        </Flex>
      ))}
    </Box>
  );
};

function handleRenameClick(resumeId: string) {
  const name = window.prompt('Enter a new name for the resume');
  if (name) {
    API.resume.updateName({
      resumeId,
      name,
    });
  }
}

export default ResumesList;
